import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  MenuItem,
  InputAdornment,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import axiosInstance from '../../../api/axiosInstance';
import base_url from '../../../constants';
import useNotification from '../../common/Notification';

const AutomationScripts = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [automationEnabledFilter, setAutomationEnabledFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [meta, setMeta] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1,
  });
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [editFormData, setEditFormData] = useState({
    automation_enabled: false,
    run_interval_minutes: 60,
    next_run_at: '',
    process_status: 'ACTIVE',
    message_failed_count: 0,
  });
  const notify = useNotification();

  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        per_page: rowsPerPage.toString(),
      });
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      if (automationEnabledFilter !== '') params.append('automation_enabled', automationEnabledFilter);

      const response = await axiosInstance.get(
        `${base_url}/whatsapp/business/admin/automation?${params}`
      );

      if (response.data && response.data.success) {
        setConfigs(response.data.data.data || []);
        setMeta({
          current_page: response.data.data.current_page,
          per_page: response.data.data.per_page,
          total: response.data.data.total,
          last_page: response.data.data.last_page,
        });
      } else {
        throw new Error('Failed to fetch automation configurations');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch automation configurations';
      setError(errorMessage);
      console.error('Error fetching configs:', err);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, statusFilter, automationEnabledFilter]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  const handleEditClick = (config) => {
    setSelectedConfig(config);
    setEditFormData({
      automation_enabled: config.automation_enabled || false,
      run_interval_minutes: config.run_interval_minutes || 60,
      next_run_at: config.next_run_at ? new Date(config.next_run_at).toISOString().slice(0, 16) : '',
      process_status: config.process_status || 'ACTIVE',
      message_failed_count: config.message_failed_count || 0,
    });
    setEditModalOpen(true);
  };

  const handleUpdate = async () => {
    if (!selectedConfig) return;

    try {
      const payload = {
        automation_enabled: editFormData.automation_enabled,
        run_interval_minutes: editFormData.run_interval_minutes,
        process_status: editFormData.process_status,
        message_failed_count: editFormData.message_failed_count,
      };

      if (editFormData.next_run_at) {
        payload.next_run_at = editFormData.next_run_at;
      }

      const response = await axiosInstance.put(
        `${base_url}/whatsapp/business/admin/automation/${selectedConfig.user_id}`,
        payload
      );

      if (response.data && response.data.success) {
        notify('Automation configuration updated successfully', 'success');
        setEditModalOpen(false);
        fetchConfigs();
      } else {
        throw new Error('Failed to update configuration');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update automation configuration';
      notify(errorMessage, 'error');
      console.error('Error updating config:', err);
    }
  };

  const handleResetStats = async (userId) => {
    if (!window.confirm('Are you sure you want to reset statistics for this user?')) {
      return;
    }

    try {
      const response = await axiosInstance.post(
        `${base_url}/whatsapp/business/admin/automation/${userId}/reset-stats`
      );

      if (response.data && response.data.success) {
        notify('Automation statistics reset successfully', 'success');
        fetchConfigs();
      } else {
        throw new Error('Failed to reset statistics');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to reset statistics';
      notify(errorMessage, 'error');
      console.error('Error resetting stats:', err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Box>
      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search by user name or email..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 300 }}
          />
          <TextField
            select
            label="Status"
            size="small"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="ACTIVE">Active</MenuItem>
            <MenuItem value="INACTIVE">Inactive</MenuItem>
          </TextField>
          <TextField
            select
            label="Automation Enabled"
            size="small"
            value={automationEnabledFilter}
            onChange={(e) => {
              setAutomationEnabledFilter(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="1">Enabled</MenuItem>
            <MenuItem value="0">Disabled</MenuItem>
          </TextField>
          <IconButton onClick={fetchConfigs} color="primary">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>User ID</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>User Name</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Email</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Automation Enabled</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Last Run Status</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Total Runs</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Success</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Failed</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Message Failed Count</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Next Run At</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={12} align="center" sx={{ py: 3 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : configs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={12} align="center" sx={{ py: 3 }}>
                  <Typography color="textSecondary">No configurations found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              configs.map((config) => (
                <TableRow key={config.id} hover>
                  <TableCell>{config.user_id}</TableCell>
                  <TableCell>{config.user?.name || '-'}</TableCell>
                  <TableCell>{config.user?.email || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={config.status}
                      size="small"
                      color={config.status === 'ACTIVE' ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={config.automation_enabled ? 'Yes' : 'No'}
                      size="small"
                      color={config.automation_enabled ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={config.last_run_status || '-'}
                      size="small"
                      color={config.last_run_status === 'SUCCESS' ? 'success' : config.last_run_status === 'FAILED' ? 'error' : 'default'}
                    />
                  </TableCell>
                  <TableCell>{config.total_runs || 0}</TableCell>
                  <TableCell>{config.total_success || 0}</TableCell>
                  <TableCell>{config.total_failed || 0}</TableCell>
                  <TableCell>{config.message_failed_count || 0}</TableCell>
                  <TableCell>{formatDate(config.next_run_at)}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleEditClick(config)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <Button
                      size="small"
                      color="secondary"
                      onClick={() => handleResetStats(config.user_id)}
                      sx={{ ml: 1 }}
                    >
                      Reset Stats
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={meta.total}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[5, 10, 15, 25, 50]}
      />

      {/* Edit Modal */}
      <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Automation Configuration</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={editFormData.automation_enabled}
                  onChange={(e) => setEditFormData({ ...editFormData, automation_enabled: e.target.checked })}
                />
              }
              label="Automation Enabled"
            />
            <TextField
              label="Run Interval (minutes)"
              type="number"
              value={editFormData.run_interval_minutes}
              onChange={(e) => setEditFormData({ ...editFormData, run_interval_minutes: parseInt(e.target.value) || 60 })}
              inputProps={{ min: 1, max: 1440 }}
              fullWidth
            />
            <TextField
              label="Next Run At"
              type="datetime-local"
              value={editFormData.next_run_at}
              onChange={(e) => setEditFormData({ ...editFormData, next_run_at: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              select
              label="Process Status"
              value={editFormData.process_status}
              onChange={(e) => setEditFormData({ ...editFormData, process_status: e.target.value })}
              fullWidth
            >
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="INACTIVE">Inactive</MenuItem>
            </TextField>
            <TextField
              label="Message Failed Count"
              type="number"
              value={editFormData.message_failed_count}
              onChange={(e) => setEditFormData({ ...editFormData, message_failed_count: parseInt(e.target.value) || 0 })}
              inputProps={{ min: 0 }}
              fullWidth
              helperText="Number of messages that failed"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditModalOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdate} variant="contained">Update</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AutomationScripts;

